import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import type { WireArticle } from '../../api/types'
import { MyWireArticleRow } from './MyWireArticleRow'

const PAGE_SIZE = 10

interface ArticleListViewProps {
  articles: WireArticle[]
  isLoading: boolean
  /** False while pages are still being auto-fetched; controls count display. */
  isFullyLoaded: boolean
  title: string
  icon: React.ElementType
  emptyTitle: string
  emptySubtitle?: string
  /** Optional banner rendered between the heading and the search box (e.g. undo notification). */
  alert?: React.ReactNode
  /** When provided, each row shows a remove (trash) button that calls this instead of the bookmark toggle. */
  onRemove?: (article: WireArticle) => void
}

export function ArticleListView({
  articles,
  isLoading,
  isFullyLoaded,
  title,
  icon: Icon,
  emptyTitle,
  emptySubtitle,
  alert,
  onRemove,
}: ArticleListViewProps) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  useEffect(() => { setPage(0) }, [search])

  const query = search.trim().toLowerCase()
  const filtered = query
    ? articles.filter((a) => a.title.toLowerCase().includes(query))
    : articles

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <div className="mx-auto max-w-5xl px-8 py-12">
      <Link
        to="/wire/me"
        className="flex items-center gap-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg"
      >
        <ArrowLeft size={12} aria-hidden="true" />
        Back to My Wire
      </Link>

      <div className="mb-6 mt-4 flex items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-fg-secondary" aria-hidden="true" />
          <h1 className="font-zodiak text-3xl font-bold text-fg">{title}</h1>
        </div>
        {!isLoading && (
          <p className="shrink-0 pb-0.5 text-sm text-fg-secondary">
            {isFullyLoaded
              ? query
                ? `${filtered.length} of ${articles.length}`
                : `${articles.length} article${articles.length !== 1 ? 's' : ''}`
              : <span className="opacity-40">Loading…</span>
            }
          </p>
        )}
      </div>

      {alert}

      {!isLoading && articles.length > 0 && (
        <div className="relative mb-6">
          <Search
            size={13}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-secondary opacity-50"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search titles…"
            className="w-full rounded-md border border-line bg-canvas py-2 pl-9 pr-8 text-sm text-fg placeholder:text-fg-secondary/50 focus:border-line-strong focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-fg-secondary opacity-50 transition-opacity hover:opacity-100"
            >
              <X size={13} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-[76px] animate-pulse rounded-lg border border-line bg-fill" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line bg-canvas text-center">
          <Icon size={20} className="text-fg-secondary opacity-30" aria-hidden="true" />
          <p className="mt-2 text-xs font-semibold text-fg-secondary opacity-60">
            {query ? `No articles match "${query}".` : emptyTitle}
          </p>
          {!query && emptySubtitle && (
            <p className="text-[11px] text-fg-secondary opacity-40">{emptySubtitle}</p>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {visible.map((article) => (
              <MyWireArticleRow key={article.id} article={article} onRemove={onRemove} />
            ))}
          </div>

          {pageCount > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-canvas text-fg-secondary transition-colors hover:border-line-strong hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              <span className="text-[11px] font-medium text-fg-secondary">
                Page {page + 1} of {pageCount}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-canvas text-fg-secondary transition-colors hover:border-line-strong hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
